import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { useGetPredictionProducts } from '@/api/prediction.queries';
import type { PredictionComplexProduct, PredictionComplexContract } from '@/types/prediction.types';
import type { ForecastTraderDetailedMarket, ForecastTraderStrike } from '@/types/forecasttrader.types';
import { CONDITIONAL_CATEGORY_ID } from '@/markets/constants';
import { formatMeasuredPeriod } from '@/utils/intradayContracts';

export function mapComplexContractToStrike(
  contract: PredictionComplexContract,
  preferredExchange: string,
): ForecastTraderStrike | null {
  const matched = contract.contracts.find((c) => c.exchange === preferredExchange) ?? contract.contracts[0];
  if (!matched) return null;
  const strike: ForecastTraderStrike = {
    strike_label: contract.strike_label,
    measured_period: formatMeasuredPeriod(contract),
    last_trade_time: matched.last_trade_time ?? 0,
    conid_yes: matched.conid_yes,
    conid_no: matched.conid_no,
    exchange: matched.exchange,
    party: contract.party,
    restricted: contract.restricted,
    ...(contract.ask_yes !== undefined && { ask_yes: contract.ask_yes }),
    ...(contract.ask_no !== undefined && { ask_no: contract.ask_no }),
    ...(contract.bid_yes !== undefined && { bid_yes: contract.bid_yes }),
    ...(contract.bid_no !== undefined && { bid_no: contract.bid_no }),
    ...(contract.open_interest !== undefined && { open_interest: contract.open_interest }),
    ...(contract.is_center !== undefined && { is_center: contract.is_center }),
  };
  if (contract.contracts.length > 1) {
    strike.yesConidExPairs = contract.contracts.map((c) => `${c.conid_yes}@${c.exchange}`);
    strike.noConidExPairs = contract.contracts.map((c) => `${c.conid_no}@${c.exchange}`);
  }
  return strike;
}

export function reorderStrikesByCenter(strikes: ForecastTraderStrike[]): ForecastTraderStrike[] {
  if (strikes.length < 2) return strikes;
  const centerIdx = strikes.findIndex((s) => s.is_center);
  if (centerIdx === -1) return strikes;

  if (centerIdx !== strikes.length - 1) {
    const center = strikes[centerIdx];
    const next = strikes[centerIdx + 1];
    const rest = strikes.filter((_, i) => i !== centerIdx && i !== centerIdx + 1);
    return [center, next, ...rest];
  } else {
    const prev = strikes[centerIdx - 1];
    const center = strikes[centerIdx];
    const rest = strikes.filter((_, i) => i !== centerIdx && i !== centerIdx - 1);
    return [prev, center, ...rest];
  }
}

export function mapComplexProductToDetailedMarket(
  product: PredictionComplexProduct,
  category?: string,
): ForecastTraderDetailedMarket {
  const representative = product.products[0];
  const exchanges = product.products.map((p) => p.exchange);
  const productConids = product.products.map((p) => p.conid);
  const productConidExPairs = product.products.map((p) => `${p.conid}@${p.exchange}`);
  const open_interest = product.open_interest ?? representative?.open_interest;
  return {
    name: product.name,
    symbol: '',
    exchange: representative.exchange,
    conid: representative.conid,
    strike_count: product.strike_count,
    logo_category: product.logo_category,
    category,
    strikes: reorderStrikesByCenter(
      product.complex_contracts
        .map((cc) => mapComplexContractToStrike(cc, representative.exchange))
        .filter((s): s is ForecastTraderStrike => s !== null),
    ),
    exchanges,
    productConids,
    productConidExPairs,
    open_interest,
    restricted: product.restricted,
    isConditional: product.is_conditional === true || category === CONDITIONAL_CATEGORY_ID,
  };
}

export function mapProductsResponse(
  complexProducts: PredictionComplexProduct[],
  category?: string,
): ForecastTraderDetailedMarket[] {
  return complexProducts.map((p) => mapComplexProductToDetailedMarket(p, category));
}

export const usePredictionFeatured = (categories?: MaybeRefOrGetter<string>) => {
  const params = computed(() => ({
    endpoint: 'featured' as const,
    categories: categories ? toValue(categories) || undefined : undefined,
  }));

  const { data, isLoading, error } = useGetPredictionProducts(params);

  const mapped = computed(() => {
    if (!data.value?.complex_products) return null;
    const cat = categories ? toValue(categories) || undefined : undefined;
    return { markets: mapProductsResponse(data.value.complex_products, cat) };
  });

  const totalOpenInterest = computed(() => data.value?.open_interest ?? 0);

  return { data: mapped, isLoading, error, totalOpenInterest };
};

export const usePredictionPopular = (
  sortBy?: MaybeRefOrGetter<'name' | 'open_interest' | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  const params = computed(() => ({
    endpoint: 'popular' as const,
    sortBy: sortBy ? toValue(sortBy) : undefined,
  }));

  const { data, isLoading, error } = useGetPredictionProducts(params, { enabled });

  const mapped = computed(() => {
    if (!data.value?.complex_products) return null;
    return { markets: mapProductsResponse(data.value.complex_products) };
  });

  const totalOpenInterest = computed(() => data.value?.open_interest ?? 0);

  return { data: mapped, isLoading, error, totalOpenInterest };
};

export const usePredictionList = (
  categories: MaybeRefOrGetter<string>,
  sortBy?: MaybeRefOrGetter<'name' | 'open_interest' | undefined>,
  enabled?: MaybeRefOrGetter<boolean>,
  /**
   * Optional: the single leaf category id to attach to each resulting market
   * (used downstream for icon URL `category` query param). When omitted, falls
   * back to the full `categories` string passed to the API.
   */
  displayCategory?: MaybeRefOrGetter<string | undefined>,
) => {
  const params = computed(() => ({
    endpoint: 'list' as const,
    categories: toValue(categories) || undefined,
    sortBy: sortBy ? toValue(sortBy) : undefined,
  }));

  const { data, isLoading, error } = useGetPredictionProducts(params, enabled !== undefined ? { enabled } : undefined);

  const markets = computed(() => {
    if (!data.value?.complex_products) return [];
    const categoryForMarket =
      (displayCategory ? toValue(displayCategory) : undefined) || toValue(categories) || undefined;
    return mapProductsResponse(data.value.complex_products, categoryForMarket);
  });

  const totalOpenInterest = computed(() => data.value?.open_interest ?? 0);

  return { data: markets, isLoading, error, totalOpenInterest };
};
// last modified by AI tools on 2026-10-09
